import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { Query, Mutation } from '@apollo/client/react/components'
import { Formik, Form, Field } from 'formik'
import { withRouter } from 'react-router'
import ReactRouterPropTypes from 'react-router-prop-types'
import * as Yup from 'yup'

import { Notification } from '@local/do-secundo-notification'
import { ModeRedirect } from '../../ModeRouter/utils'
import { Button, ButtonType, ButtonVariant } from '@local/do-secundo-button'
import { Input } from '@local/do-secundo-form'
import {
  IS_RESET_TOKEN_VALID,
  RESET_PASSWORD
} from './ResetPasswordModal.graphql'
import { ErrorComponent } from '@local/do-secundo-error'
import { Progress } from '@local/do-secundo-progress'
import { PoweredByToastModal } from '../../PoweredByToastModal/PoweredByToastModal'
import {
  importSuccessToast,
  useAuth,
  showLoggedInNotification
} from '../../AuthProvider/AuthProvider'
import { dataByTypename } from '../../../utils/apollo-helpers'
import { passwordSchema } from '../../../utils/form-schemas'
import {
  isPasswordlessLoggedIn,
  useImportAccount,
  useIsAccountLinkingEnabled,
  FROM_LOCATION,
  SEARCH_PARAMS
} from '@local/do-secundo-passwordless-authentication'

export const ResetPasswordModal = withRouter(
  ({ onClose: closeModal, location, history }) => {
    const search = new URLSearchParams(location.search)
    const resetToken = search.get('resetToken')
    const isFromEmailDetected =
      search.get(SEARCH_PARAMS.resetFrom) === FROM_LOCATION.emailDetected
    const { user, authenticate } = useAuth()
    const ACCOUNT_LINKING_ENABLED = useIsAccountLinkingEnabled()
    const accountLinkingAndPwlessLoggedIn =
      ACCOUNT_LINKING_ENABLED && isPasswordlessLoggedIn()
    const handleImportAccount = useImportAccount()

    const guestName = user?.firstName
    const onClose = React.useCallback(() => {
      closeModal()
      if (accountLinkingAndPwlessLoggedIn && isFromEmailDetected) {
        showLoggedInNotification(guestName)
      }
    }, [
      accountLinkingAndPwlessLoggedIn,
      isFromEmailDetected,
      closeModal,
      guestName
    ])

    return (
      <PoweredByToastModal header='Reset Password' onClose={onClose}>
        <Query query={IS_RESET_TOKEN_VALID} variables={{ resetToken }}>
          {({ data, error, loading, refetch }) => {
            if (loading) {
              return <Progress />
            }
            if (error) return <ErrorComponent error={error} retry={refetch} />
            const { isResetTokenValid } = data
            return (
              <Mutation mutation={RESET_PASSWORD}>
                {(reset, { loading: resetLoading, data = {} }) => {
                  const { ResetPasswordError } = dataByTypename(
                    data.resetPassword
                  )

                  if (
                    !isResetTokenValid ||
                    (ResetPasswordError &&
                      ResetPasswordError.code === 'INVALID_TOKEN')
                  ) {
                    return (
                      <ModeRedirect mode='forgot' state={{ expired: true }} />
                    )
                  }
                  return (
                    <Formik
                      initialValues={{ newPassword: '' }}
                      validationSchema={Yup.object().shape({
                        newPassword: passwordSchema
                      })}
                      onSubmit={async ({ newPassword }) => {
                        const input = {
                          variables: {
                            input: {
                              newPassword,
                              resetToken
                            }
                          }
                        }
                        const resp = await reset(input)
                        const { AuthenticationResponse } = dataByTypename(
                          resp.data.resetPassword
                        )
                        if (AuthenticationResponse) {
                          const authenticateInput = {
                            variables: {
                              input: {
                                ...AuthenticationResponse,
                                forAccountLinking: ACCOUNT_LINKING_ENABLED
                              }
                            }
                          }
                          await authenticate(authenticateInput)

                          if (ACCOUNT_LINKING_ENABLED) {
                            if (!isPasswordlessLoggedIn()) {
                              history.push('?mode=create', {
                                from: FROM_LOCATION.legacyLogin
                              })
                              return
                            } else {
                              await handleImportAccount({
                                onSuccess: () => {
                                  onClose()
                                  importSuccessToast()
                                }
                              })
                              return
                            }
                          }

                          onClose()
                        }
                      }}
                    >
                      {({ isValid }) => (
                        <Form>
                          {ResetPasswordError && (
                            <Notification severity='error'>
                              {ResetPasswordError.message}
                            </Notification>
                          )}
                          <Field
                            autoFocus
                            id='new-password'
                            name='newPassword'
                            type='password'
                            label='new password'
                            component={Input}
                            data-testid='new-password'
                          />
                          <Button
                            type={ButtonType.SUBMIT}
                            variant={ButtonVariant.PRIMARY}
                            responsive
                            disabled={!isValid}
                            loading={resetLoading}
                          >
                            Change
                          </Button>
                        </Form>
                      )}
                    </Formik>
                  )
                }}
              </Mutation>
            )
          }}
        </Query>
      </PoweredByToastModal>
    )
  }
)

ResetPasswordModal.displayName = 'ResetPasswordModal'

ResetPasswordModal.propTypes = {
  onClose: PropTypes.func,
  location: ReactRouterPropTypes.location
}
